import React, { useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

export interface FilterListItem<T = number> {
    label: string;
    value: T;
    color?: string;
}

interface Props<T> {
    items: FilterListItem<T>[];
    value: T[];
    setValue: (value: T[]) => void;
    visibleLimit?: number;
    label?: string;
    isLoading?: boolean;
    disabled?: boolean;
}

function FilterBadgeSearchList<T extends string | number>({ items, value, setValue, visibleLimit = 10, label, isLoading, disabled }: Props<T>) {
    const { styles, theme } = useStyles(styleSheet);
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(false);

    const selectedItems = items.filter(item => value.includes(item.value));

    const filtered = items.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const toggleItem = (id: T) => {
        if (value.includes(id)) {
            setValue(value.filter(v => v !== id));
        } else {
            setValue([...value, id]);
        }
    };

    const removeItem = (id: T) => {
        setValue(value.filter(v => v !== id));
    };

    const visibleSelected = showAll ? selectedItems : selectedItems.slice(0, visibleLimit);
    const hiddenCount = selectedItems.length - visibleLimit;

    return (
        <View style={[styles.container, disabled && styles.containerDisabled]}>
            {label && <MyAppText style={styles.filterLabel}>{label}</MyAppText>}

            {items.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                        editable={!disabled}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('selected')}: {value.length}
                </MyAppText>
                {value.length === 0 ? (
                    <TouchableOpacity onPress={() => setValue(filtered.map(i => i.value))} disabled={disabled}>
                        <MyAppText style={[styles.actionText, disabled && styles.actionTextDisabled]}>
                            {translate('selectAll')}
                        </MyAppText>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity onPress={() => setValue([])} disabled={disabled}>
                        <MyAppText style={[styles.actionText, disabled && styles.actionTextDisabled]}>
                            {translate('clearSelection')}
                        </MyAppText>
                    </TouchableOpacity>
                )}
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView
                    style={styles.searchResults}
                    contentContainerStyle={{ gap: 8 }}
                    nestedScrollEnabled
                >
                    {filtered.map((item) => {
                        const isSelected = value.includes(item.value);
                        return (
                            <TouchableOpacity
                                key={String(item.value)}
                                style={styles.row}
                                onPress={() => toggleItem(item.value)}
                                disabled={disabled}
                            >
                                <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                                    {isSelected && <FontAwesomeIcon icon={faCheck as IconDefinition} fontSize={10} color={theme.colors.white['500']} />}
                                </View>
                                <MyAppText style={styles.label}>{item.label}</MyAppText>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            )}

            {selectedItems.length > 0 && (
                <>
                    <View style={styles.badgesContainer}>
                        {visibleSelected.map((item) => (
                            <View key={String(item.value)} style={styles.badge}>
                                <MyAppText style={styles.badgeText}>{item.label}</MyAppText>
                                <TouchableOpacity onPress={() => removeItem(item.value)} disabled={disabled}>
                                    <FontAwesomeIcon icon={faXmark as IconDefinition} fontSize={12} color={theme.colors.labelColor} />
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>

                    {selectedItems.length > visibleLimit && (
                        <TouchableOpacity onPress={() => setShowAll(!showAll)}>
                            <MyAppText style={styles.toggleText}>
                                {showAll ? translate('seeLess') : `${translate('seeAll')} (+${hiddenCount})`}
                            </MyAppText>
                        </TouchableOpacity>
                    )}
                </>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: { gap: 12 },
    containerDisabled: {
        opacity: 0.5,
    },
    actionTextDisabled: {
        opacity: 0.4,
    },
    filterLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    actionText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchResults: {
        maxHeight: 208,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    badgesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.neutral['200'],
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
    },
    badgeText: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    toggleText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
}));

export default FilterBadgeSearchList;
